import React, { useRef } from 'react';
import './SegmentedControl.css';

// ─── SegmentedControl ───────────────────────────────────────────────────────
// A row of joined buttons where exactly one option is selected (e.g. slide size:
// hidden / default / large). Behaves as a radio group: one tab stop, and the
// arrow keys (and Home/End) move the selection.
//
// Props:
//   options   – [{ value, label, icon, className }]: `icon` (a node) is shown in the button;
//               `label` is its accessible name and hover text, and is shown as
//               text when there's no icon (or alongside it, with showLabels);
//               `className` is added to that option's button (e.g. to colour it)
//   value     – the selected option's value
//   onChange  – (value) => void
//   ariaLabel – accessible name for the whole group (e.g. "Slide size")
//   size      – 'sm' | 'md' (default 'md')
//   fullWidth – stretch to the container's width, with every option the same
//               width (long text labels are shortened with "…")
//   showLabels – show the label text next to each icon too; when the control is
//               too narrow for it, only the icons are shown (labels on hover)
//   className – extra class on the group
export default function SegmentedControl({ options, value, onChange, ariaLabel, size = 'md', fullWidth = false, showLabels = false, className }) {
  const buttonRefs = useRef([]);
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));

  const select = (index) => {
    const option = options[index];
    if (!option) return;
    if (option.value !== value) onChange?.(option.value);
    buttonRefs.current[index]?.focus();
  };

  const onKeyDown = (e) => {
    const last = options.length - 1;
    const moves = {
      ArrowRight: selectedIndex === last ? 0 : selectedIndex + 1,
      ArrowDown: selectedIndex === last ? 0 : selectedIndex + 1,
      ArrowLeft: selectedIndex === 0 ? last : selectedIndex - 1,
      ArrowUp: selectedIndex === 0 ? last : selectedIndex - 1,
      Home: 0,
      End: last,
    };
    if (e.key in moves) {
      e.preventDefault();
      select(moves[e.key]);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`segmented-control segmented-control--${size}${fullWidth ? ' segmented-control--full' : ''}${showLabels ? ' segmented-control--labels' : ''}${className ? ` ${className}` : ''}`}
      onKeyDown={onKeyDown}
    >
      {options.map((option, i) => {
        const checked = i === selectedIndex;
        return (
          <button
            key={String(option.value)}
            ref={(el) => { buttonRefs.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={option.icon ? option.label : undefined}
            title={option.icon ? option.label : undefined}
            tabIndex={checked ? 0 : -1}
            className={`segmented-control-option${checked ? ' is-selected' : ''}${option.className ? ` ${option.className}` : ''}`}
            onClick={() => select(i)}
          >
            {/* Text sits in its own element so it can be shortened with "…". */}
            {option.icon}
            {(!option.icon || showLabels) && <span className="segmented-control-label">{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}
